/**
 * LRU Cache
 *
 * As the cache for this module can persist for the lifetime of the application,
 * which could potentially last hours or even days, we want to limit the number
 * of items we store in our cache as to not consume too much memory. For this reason
 * we will implement a simple LRU cache.
 */
type CacheOptions = {
  maxSize: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Listener = (value: any) => void;

const store = Symbol('store');
const listeners = Symbol('listeners');

export class Cache {
  // Assign store as a symbol key to make it impossible to access or manipulate
  // its internal values from outside of the class.
  [store] = new Map();
  // Assign listeners as a symbol so it can't be accessed from outside the class.
  [listeners]: { [key: string]: Listener[] } = {};
  maxSize: number;

  constructor({ maxSize }: CacheOptions = { maxSize: 1024 }) {
    // Initialize the store with the max size.
    this.maxSize = maxSize;
  }

  set(key: string, value: unknown) {
    if (this.maxSize === 0) return;
    // The key already exists so we want to reset it's position to the
    // end of the queue.
    if (this[store].has(key)) {
      this[store].delete(key);
      this[store].set(key, value);

      this.notifyListeners(key, value);
      return;
    }

    // We have too many things in our cache, we need to remove one entry.
    if (this[store].size >= this.maxSize) {
      // Since maps store their keys in order as a stateful iterator,
      // we can iterate over the keys to find the next key to delete
      const keyToDelete = this[store].keys().next().value;
      this[store].delete(keyToDelete);
    }

    this.notifyListeners(key, value);

    this[store].set(key, value);
  }

  get(key: string) {
    const value = this[store].get(key);

    // In an LRU Cache, we want keys that are accessed to be deleted last,
    // so we want to dequeue that key and push it to the front of our list.
    // We can do that by deleting the key and re-setting it.
    if (value) {
      this[store].delete(key);
      this[store].set(key, value);
    }

    return value;
  }

  // Remove a key from the cache if it exists.
  delete(key: string) {
    const value = this[store].get(key);

    if (value) {
      this[store].delete(key);
      this.notifyListeners(key);
    }

    return value;
  }

  // Return the number of items in our cache.
  size() {
    return this[store].size;
  }

  // Subscribe to changes for a specific key in our cache.
  subscribeToKey<V>(key: string, listener: Listener) {
    const subscriptions = this[listeners][key] || [];

    this[listeners][key] = [...subscriptions, listener];

    return () => {
      this[listeners][key] = this[listeners][key].filter((l) => l !== listener);
    };
  }

  // When a key changes, we want to run the array of listeners for that key,
  // passing the latest value as a parameter.
  notifyListeners<V>(key: string, value?: V) {
    const subscriptions = this[listeners][key];

    if (subscriptions) {
      subscriptions.forEach((subscription) => subscription(value));
    }
  }
}
